/**
 * useErrorToast Hook
 *
 * Custom hook for managing error toast state in onboarding components.
 * Automatically syncs error states from Redux selectors to local toast state.
 */
import { useState, useCallback, useEffect, useMemo } from 'react';

/**
 * Props for Snackbar component - matches the Snackbar interface
 */
interface SnackbarProps {
  readonly message: string;
  readonly isVisible: boolean;
  readonly onClose: () => void;
  readonly type: 'error';
  readonly position: 'center' | 'default' | 'top-center';
  readonly duration: number;
}

/**
 * Options for the useErrorToast hook
 */
interface UseErrorToastOptions {
  /** Array of error states to watch. Any non-null error triggers the toast */
  readonly errorStates: ReadonlyArray<string | null>;
  /** Auto-hide duration in milliseconds. Default: 5000ms */
  readonly duration?: number;
  /** Position of the toast. Default: 'center' */
  readonly position?: 'center' | 'default' | 'top-center';
}

/**
 * Return value from useErrorToast hook
 */
interface UseErrorToastReturn {
  /** Props to spread onto the Snackbar component */
  readonly snackbarProps: SnackbarProps;
  /** Function to manually show an error toast */
  readonly showError: (message: string) => void;
  /** Function to manually hide the toast */
  readonly hideError: () => void;
  /** Current error message (or null if hidden) */
  readonly errorMessage: string | null;
}

/**
 * Custom hook for managing error toast state
 *
 * This hook:
 * 1. Watches multiple Redux error selectors and shows toast when any error occurs
 * 2. Provides a manual showError/hideError API for local errors
 * 3. Returns snackbarProps that can be spread directly onto the Snackbar component
 *
 * @param options - Configuration options
 * @returns Object with snackbarProps, showError, hideError, and current errorMessage
 *
 * @example
 * ```tsx
 * const configError = useAppSelector(selectOnboardingConfigError);
 * const searchError = useAppSelector(selectDestinationSearchError);
 *
 * const { snackbarProps, showError } = useErrorToast({
 *   errorStates: [configError, searchError],
 *   duration: 5000,
 * });
 *
 * // In catch block:
 * showError('Failed to submit');
 *
 * // In JSX:
 * <Snackbar {...snackbarProps} />
 * ```
 */
export function useErrorToast(options: UseErrorToastOptions): UseErrorToastReturn {
  const { errorStates, duration = 5000, position = 'center' } = options;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Watch all error states and show toast when any changes to non-null
  useEffect(() => {
    for (const error of errorStates) {
      if (error !== null) {
        setErrorMessage(error);
        return;
      }
    }
  }, [errorStates]);

  const hideError = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const showError = useCallback((message: string) => {
    setErrorMessage(message);
  }, []);

  const snackbarProps: SnackbarProps = useMemo(
    () => ({
      message: errorMessage ?? '',
      isVisible: errorMessage !== null,
      onClose: hideError,
      type: 'error' as const,
      position,
      duration,
    }),
    [errorMessage, hideError, position, duration],
  );

  return {
    snackbarProps,
    showError,
    hideError,
    errorMessage,
  };
}
